import { useEffect, useRef, useState } from "react";
import { Save, Star, Upload } from "lucide-react";

import { useStore } from "../context/StoreContext";
import { Field, GhostBtn, INPUT, PrimaryBtn, Select, Sheet, Spinner, Toggle } from "./ui";

const DOC_TYPES = ["Overview", "Feature", "Changelog", "FAQ", "Case study", "Other"];
const ACCEPT = ".txt,.md,.markdown,.pdf,.doc,.docx,.xls,.xlsx,.csv";

export default function DocEditor({ open, doc, onClose, onSave }) {
  const { extractDocument, showToast } = useStore();
  const [draft, setDraft] = useState(doc);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    setDraft(doc);
    setImportError("");
  }, [doc]);

  if (!open || !draft) return null;

  const upd = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const importFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-importing the same file
    if (!f) return;
    setImporting(true);
    setImportError("");
    try {
      const { title, content, truncated } = await extractDocument(f);
      setDraft((d) => ({
        ...d,
        content,
        title: d.title?.trim() ? d.title : title,
      }));
      showToast(truncated ? "Imported (trimmed to fit)" : "File imported");
    } catch (err) {
      setImportError(
        (err && err.message ? err.message.replace(/^\d+\s*/, "") : "") ||
          "Could not read that file.",
      );
    }
    setImporting(false);
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      wide
      title={draft._isNew ? "Add document" : "Edit document"}
      footer={
        <>
          <GhostBtn onClick={onClose}>Cancel</GhostBtn>
          <PrimaryBtn
            onClick={() => onSave(draft)}
            disabled={!draft.title?.trim() || !draft.content?.trim()}
          >
            <Save size={16} />
            Save document
          </PrimaryBtn>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Title">
            <input
              className={INPUT}
              placeholder="e.g. Feature spec, Smart Scheduling"
              value={draft.title || ""}
              onChange={(e) => upd("title", e.target.value)}
            />
          </Field>
          <Field label="Type">
            <Select
              value={draft.type || "Feature"}
              onChange={(v) => upd("type", v)}
              options={DOC_TYPES}
            />
          </Field>
        </div>

        <div className="border border-border rounded-xl px-3.5 py-3 bg-surface">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Star size={16} className="text-foreground" />
              <div className="text-sm font-medium text-foreground">
                Highlight as a recent update
              </div>
            </div>
            <Toggle checked={!!draft.is_update} onChange={(v) => upd("is_update", v)} />
          </div>
          <div className="text-xs text-muted leading-relaxed mt-2">
            Turn this on for a launch, a new feature, or any change you want featured right now.
            remifo puts these first and makes sure at least one post in the next calendar is about
            it, so announcements never get buried. Leave it off for evergreen background docs.
          </div>
        </div>

        <Field label="Content" hint="paste text, or import a file">
          <textarea
            className={`${INPUT} min-h-[200px] leading-relaxed`}
            placeholder="Describe the product, feature, or update in plain language. The more concrete, the better the posts."
            value={draft.content || ""}
            onChange={(e) => upd("content", e.target.value)}
          />
        </Field>

        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={importFile}
        />
        <div className="flex items-center gap-3 flex-wrap">
          <GhostBtn onClick={() => fileRef.current?.click()} disabled={importing}>
            {importing ? <Spinner size={15} /> : <Upload size={15} />}
            {importing ? "Reading file" : "Import file"}
          </GhostBtn>
          <span className="text-xs text-faint">PDF, Word, Excel, CSV, text, or Markdown</span>
        </div>
        {importError ? (
          <div className="text-xs text-muted border border-border rounded-lg px-3 py-2 bg-surface">
            {importError}
          </div>
        ) : null}
      </div>
    </Sheet>
  );
}